import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button, Spinner, Table } from 'react-bootstrap';
import { FaPrint, FaTimes, FaCheck, FaCalendarAlt, FaDownload } from 'react-icons/fa';
import './PatientReportView.css';

const PrintStyles = () => (
  <style>
    {`
      @media print {
        @page {
          size: A4 portrait;
          margin: 15mm 10mm 20mm 10mm;
        }
        
        * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        
        html, body {
          width: 210mm;
          height: 297mm;
          margin: 0;
          padding: 0;
        }
        
        .no-print,
        .report-actions-bar,
        button,
        .btn {
          display: none !important;
        }
        
        .patient-report-view {
          width: 100%;
        }
        
        /* Each page must be exactly one page height */
        .print-page {
          width: 210mm;
          min-height: 297mm;
          max-height: 297mm;
          padding: 15mm 10mm 20mm 10mm;
          margin: 0;
          box-sizing: border-box;
          page-break-after: always;
          page-break-inside: avoid;
          position: relative;
          background: white;
        }
        
        .print-page:last-child {
          page-break-after: auto;
        }
        
        .report-card {
          width: 100%;
          height: 100%;
          box-shadow: none;
          border: none;
          background: transparent;
        }
        
        .ultrasound-report-section {
          width: 100%;
          height: 100%;
        }
        
        .report-content {
          width: 100% !important;
          overflow: visible !important;
        }
        
        .clinic-header,
        .report-header,
        .report-body,
        .report-footer {
          width: 100% !important;
          overflow: visible !important;
        }
        
        /* Compact spacing for first page */
        .report-card h5 {
          font-size: 11px !important;
          margin-bottom: 3px !important;
        }
        
        .report-card p {
          font-size: 10px !important;
          margin-bottom: 2px !important;
        }
        
        .report-card .medication-table {
          font-size: 9px !important;
        }
        
        .report-card .medication-table th,
        .report-card .medication-table td {
          padding: 2px 4px !important;
          font-size: 9px !important;
        }
        
        table {
          width: 100% !important;
          border-collapse: collapse !important;
          page-break-inside: auto !important;
        }
        
        tr {
          page-break-inside: avoid !important;
          page-break-after: auto !important;
        }
        
        td, th {
          padding: 2mm !important;
          font-size: 10pt !important;
        }
        
        img {
          max-width: 60px !important;
          height: auto !important;
        }
        
        /* Ultrasound images styles */
        .ultrasound-images {
          margin: 15px 0 !important;
          page-break-inside: avoid !important;
          width: 100% !important;
        }
        .ultrasound-images table {
          width: 100% !important;
          table-layout: fixed !important;
          border-collapse: collapse !important;
        }
        .ultrasound-images tr {
          display: flex !important;
          flex-direction: row !important;
          width: 100% !important;
        }
        .ultrasound-images td {
          display: inline-block !important;
          width: 50% !important;
          max-width: 50% !important;
          float: left !important;
          box-sizing: border-box !important;
          padding: 5px !important;
        }
        .ultrasound-images img {
          max-width: 100% !important;
          border: 1px solid #ddd !important;
          background-color: #f8f9fa !important;
          padding: 4px !important;
          display: block !important;
          margin: 0 auto !important;
          height: auto !important;
        }
        
        h2, h5, p, div {
          orphans: 3;
          widows: 3;
        }
      }
    `}
  </style>
);

const PatientReportView = () => {
  const { id } = useParams();
  const reportRef = useRef();
  const [report, setReport] = useState(null);
  const [clinic, setClinic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signedDoctors, setSignedDoctors] = useState([]);

  const formatTimeWithAMPM = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours, 10);
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${period}`;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const reportRes = await axios.get(`/api/reports.php?id=${id}`);
        const reportData = {...reportRes.data};
        
        let allMedicines = [];
        try {
          const medicinesRes = await axios.get('/api/medicines');
          allMedicines = medicinesRes.data;
        } catch (err) {
          console.error("Error fetching medicines:", err);
        }
        
        if (reportData.medications && reportData.medications.length > 0) {
          reportData.medications = reportData.medications.map(med => {
            if (med.medicineName) return med;
            if (med.medicine_data && med.medicine_data.name) {
              return { ...med, medicineName: med.medicine_data.name };
            }
            if (med.medicine && typeof med.medicine === 'object' && med.medicine.name) {
              return { ...med, medicineName: med.medicine.name, medicineDetails: med.medicine };
            }
            
            const medicineId = (typeof med.medicine === 'string') 
              ? med.medicine 
              : (med.medicine && med.medicine.id) ? med.medicine.id : null;
                  
            if (medicineId) {
              const medicineDetails = allMedicines.find(m => 
                (m.id && m.id.toString() === medicineId.toString()) || 
                (m._id && m._id.toString() === medicineId.toString())
              );
              
              if (medicineDetails) {
                return { ...med, medicineName: medicineDetails.name, medicineDetails };
              }
            }
            return med;
          });
        }
        
        setReport(reportData);
        
        const clinicRes = await axios.get('/api/clinic.php');
        setClinic(clinicRes.data);
        
        let doctorsData = [];
        if (reportRes.data.signedByDoctors && reportRes.data.signedByDoctors.length > 0) {
          for (const doctorId of reportRes.data.signedByDoctors) {
            try {
              const docRes = await axios.get(`/api/doctors/${doctorId}`);
              doctorsData.push(docRes.data);
            } catch (error) {
              console.error("Error fetching doctor:", error);
            }
          }
        } else if (clinicRes.data && clinicRes.data.doctors) {
          let doctorIds = [];
          if (Array.isArray(clinicRes.data.doctors)) {
            doctorIds = clinicRes.data.doctors
              .filter(doc => doc !== null && doc !== undefined)
              .map(doc => typeof doc === 'object' && doc ? (doc._id || doc.id) : doc);
          }
          
          for (const doctorId of doctorIds) {
            try {
              if (doctorId) {
                const docRes = await axios.get(`/api/doctors/${doctorId}`);
                doctorsData.push(docRes.data);
              }
            } catch (error) {
              console.error(`Error fetching clinic doctor:`, error);
            }
          }
        }
        
        setSignedDoctors(doctorsData);
        setLoading(false);
      } catch (err) {
        console.error(err);
        setLoading(false);
      }
    };
    
    fetchData();
  }, [id]);

  const handlePrint = () => {
    const patientName = report && (report.patient || report.Patient) ? (report.patient || report.Patient).name : 'Patient';
    document.title = `${patientName} - Medical Report`;
    window.print();
    document.title = "Patient Portal";
  };

  const handleDownload = () => {
    // Use browser's native print to PDF
    const patientName = report && (report.patient || report.Patient) ? (report.patient || report.Patient).name : 'Patient';
    document.title = `Medical_Report_${patientName.replace(/\s+/g, '_')}_${id}`;
    
    // Show alert for mobile users
    if (window.innerWidth <= 768) {
      alert('Please select "Save as PDF" from the print dialog');
    }
    
    window.print();
    
    setTimeout(() => {
      document.title = "Patient Portal";
    }, 1000);
  };

  const handleClose = () => {
    window.close();
  };

  const getUrduTiming = (timing, dosage) => {
    const timingMap = {
      morning: "صبح",
      afternoon: "دوپہر",
      evening: "شام",
      night: "رات"
    };
    
    const beforeAfterMap = {
      before: "کھانے سے پہلے",
      after: "کھانے کے بعد",
      both: "کھانے سے پہلے/بعد",
      none: ""
    };
      
    if (!dosage) return timingMap[timing] || "";
    
    let before_after = 'none';
    
    if (timing === 'morning') {
      if (dosage.morningBeforeFood && dosage.morningAfterFood) before_after = 'both';
      else if (dosage.morningBeforeFood) before_after = 'before';
      else if (dosage.morningAfterFood) before_after = 'after';
    } else if (timing === 'afternoon') {
      if (dosage.afternoonBeforeFood && dosage.afternoonAfterFood) before_after = 'both';
      else if (dosage.afternoonBeforeFood) before_after = 'before';
      else if (dosage.afternoonAfterFood) before_after = 'after';
    } else if (timing === 'evening') {
      if (dosage.eveningBeforeFood && dosage.eveningAfterFood) before_after = 'both';
      else if (dosage.eveningBeforeFood) before_after = 'before';
      else if (dosage.eveningAfterFood) before_after = 'after';
    } else if (timing === 'night') {
      if (dosage.nightBeforeFood && dosage.nightAfterFood) before_after = 'both';
      else if (dosage.nightBeforeFood) before_after = 'before';
      else if (dosage.nightAfterFood) before_after = 'after';
    }
    
    if (before_after === 'none' && (dosage.beforeFood || dosage.afterFood)) {
      if (dosage.beforeFood && dosage.afterFood) before_after = 'both';
      else if (dosage.beforeFood) before_after = 'before';
      else if (dosage.afterFood) before_after = 'after';
    }
    
    return `${timingMap[timing] || ""} ${beforeAfterMap[before_after] || ""}`.trim();
  };

  const formatMedicationInstructions = (med) => {
    const timings = [];
    const { dosage } = med;
      
    if (!dosage) return "No dosage information";
    
    if (dosage.morning) {
      let ba = "";
      if (dosage.morningBeforeFood && dosage.morningAfterFood) ba = "before & after food";
      else if (dosage.morningBeforeFood) ba = "before food";
      else if (dosage.morningAfterFood) ba = "after food";
      else if (dosage.beforeFood && dosage.afterFood) ba = "before & after food";
      else if (dosage.beforeFood) ba = "before food";
      else if (dosage.afterFood) ba = "after food";
      timings.push(`Morning ${ba}`);
    }
    
    if (dosage.afternoon) {
      let ba = "";
      if (dosage.afternoonBeforeFood && dosage.afternoonAfterFood) ba = "before & after food";
      else if (dosage.afternoonBeforeFood) ba = "before food";
      else if (dosage.afternoonAfterFood) ba = "after food";
      else if (dosage.beforeFood && dosage.afterFood) ba = "before & after food";
      else if (dosage.beforeFood) ba = "before food";
      else if (dosage.afterFood) ba = "after food";
      timings.push(`Afternoon ${ba}`);
    }
    
    if (dosage.evening) {
      let ba = "";
      if (dosage.eveningBeforeFood && dosage.eveningAfterFood) ba = "before & after food";
      else if (dosage.eveningBeforeFood) ba = "before food";
      else if (dosage.eveningAfterFood) ba = "after food";
      else if (dosage.beforeFood && dosage.afterFood) ba = "before & after food";
      else if (dosage.beforeFood) ba = "before food";
      else if (dosage.afterFood) ba = "after food";
      timings.push(`Evening ${ba}`);
    }
    
    if (dosage.night) {
      let ba = "";
      if (dosage.nightBeforeFood && dosage.nightAfterFood) ba = "before & after food";
      else if (dosage.nightBeforeFood) ba = "before food";
      else if (dosage.nightAfterFood) ba = "after food";
      else if (dosage.beforeFood && dosage.afterFood) ba = "before & after food";
      else if (dosage.beforeFood) ba = "before food";
      else if (dosage.afterFood) ba = "after food";
      timings.push(`Night ${ba}`);
    }
    
    return timings.join(", ") || "No specific timing";
  };

  const formatMedicationInstructionsUrdu = (med) => {
    const timings = [];
    const { dosage } = med;
      
    if (!dosage) return "";
    
    if (dosage.morning) timings.push(getUrduTiming("morning", dosage));
    if (dosage.afternoon) timings.push(getUrduTiming("afternoon", dosage));
    if (dosage.evening) timings.push(getUrduTiming("evening", dosage));
    if (dosage.night) timings.push(getUrduTiming("night", dosage));
    
    return timings.join("، ") || "";
  };

  if (loading) {
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" />
        <p className="mt-3">Loading report...</p>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="text-center mt-5">
        <h4>Report not found</h4>
      </div>
    );
  }

  const patientData = report && (report.patient || report.Patient) ? (report.patient || report.Patient) : null;
  const doctorData = report && (report.doctor || report.Doctor) ? (report.doctor || report.Doctor) : null;
  
  const safePatientData = patientData || { 
    patientId: "Unknown",
    name: "Unknown Patient", 
    age: "?", 
    gender: "?", 
    bloodGroup: "Unknown" 
  };
  
  const safeDoctorData = doctorData || { 
    name: "Unknown Doctor" 
  };

  const formattedDate = new Date(report.date).toLocaleDateString();

  return (
    <div className="patient-report-view">
      <PrintStyles />
      
      <div className="report-actions-bar no-print">
        <Button variant="primary" onClick={handlePrint}>
          <FaPrint className="me-2" /> Print Report
        </Button>
        <Button variant="success" onClick={handleDownload} className="ms-2">
          <FaDownload className="me-2" /> Download PDF
        </Button>
        <Button variant="secondary" onClick={handleClose} className="ms-2">
          <FaTimes className="me-2" /> Close
        </Button>
      </div>

      {/* PAGE 1: Medical Report */}
      <div className="print-page">
        <div ref={reportRef} className="report-card">
        <div className="clinic-header">
          <div style={{ width: '80px', marginRight: '15px' }}>
            <img 
              src={clinic?.logo || "/logo.png"} 
              alt={clinic?.name || "Clinic Logo"} 
              style={{ maxWidth: '100%', height: 'auto' }} 
            />
          </div>
          <div>
            <h2 style={{ 
              margin: '0', 
              fontFamily: 'Georgia, serif', 
              color: '#1a5276', 
              fontWeight: 'bold',
              letterSpacing: '1px'
            }}>
              {clinic?.name || "Your Clinic Name"}
            </h2>
            <p style={{ margin: '2px 0 0 0', fontSize: '12px' }}>
              {clinic?.address || "Address"} | {clinic?.contactNumber || "Contact Information"}
            </p>
          </div>
        </div>
        
        <div className="report-content">
          <div className="report-header">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                <tr>
                  <td style={{ width: '40%', verticalAlign: 'top' }}>
                    <p style={{ marginBottom: '4px' }}>
                      <strong>Patient ID:</strong> {safePatientData.patientId}
                    </p>
                  </td>
                  <td style={{ width: '60%', verticalAlign: 'top', textAlign: 'right' }}>
                    <p style={{ marginBottom: '4px' }}>
                      <strong>Date:</strong> {formattedDate}
                    </p>
                  </td>
                </tr>
                <tr>
                  <td style={{ verticalAlign: 'top' }}>
                    <p style={{ marginBottom: '4px' }}>
                      <strong>Patient Name:</strong> {safePatientData.name}
                    </p>
                  </td>
                  <td style={{ verticalAlign: 'top', textAlign: 'right' }}>
                    <p style={{ marginBottom: '4px' }}>
                      <strong>Doctor:</strong> {safeDoctorData.name}
                    </p>
                  </td>
                </tr>
                <tr>
                  <td style={{ verticalAlign: 'top' }}>
                    <p style={{ marginBottom: '4px' }}>
                      <strong>Age/Gender:</strong> {safePatientData.age}/{safePatientData.gender}
                    </p>
                  </td>
                  <td style={{ verticalAlign: 'top', textAlign: 'right' }}>
                    {report.bloodPressure && report.bloodPressure.systolic && report.bloodPressure.diastolic && (
                      <p style={{ marginBottom: '4px' }}>
                        <strong>Blood Pressure:</strong> {report.bloodPressure.systolic}/{report.bloodPressure.diastolic} mmHg
                      </p>
                    )}
                  </td>
                </tr>
                <tr>
                  <td style={{ verticalAlign: 'top' }}>
                    <p style={{ marginBottom: '4px' }}>
                      <strong>Blood Group:</strong> {safePatientData.bloodGroup && safePatientData.bloodGroup !== "" ? safePatientData.bloodGroup : "None"}
                    </p>
                  </td>
                  <td style={{ verticalAlign: 'top', textAlign: 'right' }}>
                    {report.followUpDate && (
                      <p style={{ marginBottom: '4px' }}>
                        <FaCalendarAlt className="me-1" /><strong>Follow Up:</strong> {new Date(report.followUpDate).toLocaleDateString()}
                      </p>
                    )}
                    {report.billNumber && (
                      <p style={{ marginBottom: '4px' }}>
                        <strong>Bill #:</strong> {report.billNumber}
                      </p>
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <div className="report-body">
            <div className="mb-3">
              <h5 className="mb-2">Diagnosis</h5>
              <p className="mb-3">{report.diagnosis}</p>
              
              {report.doctorAdvice && (
                <div className="mt-3">
                  <h5 className="mb-1">Doctor's Advice</h5>
                  <p>{report.doctorAdvice}</p>
                </div>
              )}
            </div>
            
            <hr />
            
            <h5 className="text-center mb-3">Medications</h5>
            {report.medications && report.medications.length > 0 ? (
              <Table className="medication-table">
                <thead>
                  <tr>
                    <th>Medicine</th>
                    <th>Instructions</th>
                    <th>Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {report.medications.map((med, index) => {
                    let medicineName = "Unknown";
                    
                    if (med.medicineName) {
                      medicineName = med.medicineName;
                    } else if (med.medicine_data && med.medicine_data.name) {
                      medicineName = med.medicine_data.name;
                    } else if (med.medicineDetails && med.medicineDetails.name) {
                      medicineName = med.medicineDetails.name;
                    } else if (med.medicine && typeof med.medicine === 'object' && med.medicine.name) {
                      medicineName = med.medicine.name;
                    } else if (typeof med.medicine === 'string') {
                      medicineName = med.medicine;
                    }
                    
                    return (
                      <tr key={index}>
                        <td>
                          <strong>{medicineName}</strong>
                          {med.providedFromStock && (
                            <span className="ms-1">
                              <small>({med.quantity} {med.medicineDetails?.unit || med.medicine?.unit || 'units'})</small>
                              <FaCheck className="text-success ms-1" style={{fontSize: '12px'}} /> 
                            </span>
                          )}
                        </td>
                        <td>
                          <div className="bi-lingual">
                            <div>{formatMedicationInstructions(med)}</div>
                            <div className="urdu-text">{formatMedicationInstructionsUrdu(med)}</div>
                          </div>
                          {med.instructions && <div><small>Note: {med.instructions}</small></div>}
                        </td>
                        <td>{med.duration}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            ) : (
              <p className="text-center">No medications prescribed</p>
            )}
          </div>
          
          <div className="report-footer">
            <div className="footer-doctor-signature" style={{ display: 'flex', justifyContent: 'space-around', width: '100%' }}>
              {Array.from({ length: 3 }).map((_, index) => {
                const doctor = signedDoctors[index] || (index === 0 ? safeDoctorData : null);
                return (
                  <div key={index} style={{ width: '33.33%', textAlign: 'center' }}>
                    {doctor && (
                      <>
                        <p className="footer-doctor-name">Dr. {doctor.name}</p>
                        <p className="footer-doctor-qualification" style={{ whiteSpace: 'pre-line' }}>
                          {doctor.qualification || 'MBBS'}
                        </p>
                        <p className="footer-doctor-qualification" style={{ whiteSpace: 'pre-line' }}>
                          {doctor.specialization || ''}
                        </p>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
            
            <div className="footer-clinic-hours" style={{ marginBottom: '4px' }}>
              {clinic?.timings ? (
                <>Clinic Hours: {formatTimeWithAMPM(clinic.timings.open)} - {formatTimeWithAMPM(clinic.timings.close)}
                {clinic.workingDays && clinic.workingDays.length > 0 && 
                  ` | Working Days: ${clinic.workingDays.join(', ')}`
                }</>
              ) : (
                'Clinic Hours: 9:00 AM - 8:00 PM | Working Days: Monday, Tuesday, Wednesday, Thursday, Friday, Saturday'
              )}
            </div>
            
            <div className="footer-credit">
              Designed by Abdullah Arif | Contact: +92-3137323866 | Email: abdullaharif7323@gmail.com
            </div>
          </div>
        </div>
      </div>
      </div>
      
      {/* PAGE 2: Ultrasound Report */}
      {report.ultrasoundReport && report.ultrasoundReport.hasUltrasound && (
        <div className="print-page">
          <div className="ultrasound-report-section">
          <div className="clinic-header" style={{ display: 'flex', alignItems: 'center', borderBottom: '2px solid #333', paddingBottom: '10px', marginBottom: '15px' }}>
            <div style={{ width: '80px', marginRight: '15px' }}>
              <img 
                src={clinic?.logo || "/logo.png"} 
                alt={clinic?.name || "Clinic Logo"} 
                style={{ maxWidth: '100%', height: 'auto' }} 
              />
            </div>
            <div>
              <h2 style={{ 
                margin: '0', 
                fontFamily: 'Georgia, serif', 
                color: '#1a5276', 
                fontWeight: 'bold',
                letterSpacing: '1px'
              }}>
                {clinic?.name || 'Your Clinic Name'}
              </h2>
              <p style={{ margin: '2px 0 0 0', fontSize: '12px' }}>{clinic?.address || 'Address'} | {clinic?.contactNumber || 'Contact Information'}</p>
            </div>
          </div>
          
          <h3 style={{ textAlign: 'center', fontSize: '14px', marginBottom: '10px' }}>
            {report.ultrasoundReport.type} Ultrasound Report
          </h3>
          
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px' }}>
            <div>
              <p style={{ margin: '2px 0', fontSize: '11px' }}><strong>Patient:</strong> {safePatientData.name}</p>
              <p style={{ margin: '2px 0', fontSize: '11px' }}><strong>Age/Gender:</strong> {safePatientData.age}/{safePatientData.gender}</p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <p style={{ margin: '2px 0', fontSize: '11px' }}><strong>Date:</strong> {formattedDate}</p>
              <p style={{ margin: '2px 0', fontSize: '11px' }}><strong>Doctor:</strong> {safeDoctorData.name}</p>
              {report.billNumber && (
                <p style={{ margin: '2px 0', fontSize: '11px' }}><strong>Bill #:</strong> {report.billNumber}</p>
              )}
              {report.bloodPressure && report.bloodPressure.systolic && report.bloodPressure.diastolic && (
                <p style={{ margin: '2px 0', fontSize: '11px' }}><strong>Blood Pressure:</strong> {report.bloodPressure.systolic}/{report.bloodPressure.diastolic} mmHg</p>
              )}
            </div>
          </div>
          
          {report.ultrasoundReport.type === 'Obstetric' && (
            <div style={{ marginBottom: '15px' }}>
              <h4 style={{ fontSize: '12px', marginBottom: '5px' }}>Fetal Biometry</h4>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
                <thead>
                  <tr>
                    <th style={{ border: '1px solid #000', padding: '4px', textAlign: 'left', backgroundColor: '#f4f4f4' }}>Parameter</th>
                    <th style={{ border: '1px solid #000', padding: '4px', textAlign: 'left', backgroundColor: '#f4f4f4' }}>Measurement</th>
                  </tr>
                </thead>
                <tbody>
                  {report.ultrasoundReport.gestationalAge && (
                    <tr>
                      <td style={{ border: '1px solid #000', padding: '4px' }}>Gestational Age</td>
                      <td style={{ border: '1px solid #000', padding: '4px' }}>{report.ultrasoundReport.gestationalAge}</td>
                    </tr>
                  )}
                  {report.ultrasoundReport.biparietalDiameter && (
                    <tr>
                      <td style={{ border: '1px solid #000', padding: '4px' }}>Biparietal Diameter (BPD)</td>
                      <td style={{ border: '1px solid #000', padding: '4px' }}>{report.ultrasoundReport.biparietalDiameter} mm</td>
                    </tr>
                  )}
                  {report.ultrasoundReport.headCircumference && (
                    <tr>
                      <td style={{ border: '1px solid #000', padding: '4px' }}>Head Circumference (HC)</td>
                      <td style={{ border: '1px solid #000', padding: '4px' }}>{report.ultrasoundReport.headCircumference} mm</td>
                    </tr>
                  )}
                  {report.ultrasoundReport.abdominalCircumference && (
                    <tr>
                      <td style={{ border: '1px solid #000', padding: '4px' }}>Abdominal Circumference (AC)</td>
                      <td style={{ border: '1px solid #000', padding: '4px' }}>{report.ultrasoundReport.abdominalCircumference} mm</td>
                    </tr>
                  )}
                  {report.ultrasoundReport.femurLength && (
                    <tr>
                      <td style={{ border: '1px solid #000', padding: '4px' }}>Femur Length (FL)</td>
                      <td style={{ border: '1px solid #000', padding: '4px' }}>{report.ultrasoundReport.femurLength} mm</td>
                    </tr>
                  )}
                </tbody>
              </table>
              
              <h4 style={{ fontSize: '12px', marginBottom: '5px', marginTop: '15px' }}>Obstetric Parameters</h4>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
                <thead>
                  <tr>
                    <th style={{ border: '1px solid #000', padding: '4px', textAlign: 'left', backgroundColor: '#f4f4f4' }}>Parameter</th>
                    <th style={{ border: '1px solid #000', padding: '4px', textAlign: 'left', backgroundColor: '#f4f4f4' }}>Finding</th>
                  </tr>
                </thead>
                <tbody>
                  {report.ultrasoundReport.fetalHeartRate && (
                    <tr>
                      <td style={{ border: '1px solid #000', padding: '4px' }}>Fetal Heart Rate</td>
                      <td style={{ border: '1px solid #000', padding: '4px' }}>{report.ultrasoundReport.fetalHeartRate} bpm</td>
                    </tr>
                  )}
                  {report.ultrasoundReport.gestationalSac && (
                    <tr>
                      <td style={{ border: '1px solid #000', padding: '4px' }}>Gestational Sac (GS)</td>
                      <td style={{ border: '1px solid #000', padding: '4px' }}>{report.ultrasoundReport.gestationalSac} mm</td>
                    </tr>
                  )}
                  {report.ultrasoundReport.crownRumpLength && (
                    <tr>
                      <td style={{ border: '1px solid #000', padding: '4px' }}>Crown-Rump Length (CRL)</td>
                      <td style={{ border: '1px solid #000', padding: '4px' }}>{report.ultrasoundReport.crownRumpLength} mm</td>
                    </tr>
                  )}
                  {report.ultrasoundReport.cardiacFlicker && (
                    <tr>
                      <td style={{ border: '1px solid #000', padding: '4px' }}>Cardiac Flicker</td>
                      <td style={{ border: '1px solid #000', padding: '4px' }}>{report.ultrasoundReport.cardiacFlicker}</td>
                    </tr>
                  )}
                  {report.ultrasoundReport.fetalPresentation && (
                    <tr>
                      <td style={{ border: '1px solid #000', padding: '4px' }}>Fetal Presentation</td>
                      <td style={{ border: '1px solid #000', padding: '4px' }}>{report.ultrasoundReport.fetalPresentation}</td>
                    </tr>
                  )}
                  {report.ultrasoundReport.fetalLie && (
                    <tr>
                      <td style={{ border: '1px solid #000', padding: '4px' }}>Fetal Lie</td>
                      <td style={{ border: '1px solid #000', padding: '4px' }}>{report.ultrasoundReport.fetalLie}</td>
                    </tr>
                  )}
                  {report.ultrasoundReport.fetalMovement && (
                    <tr>
                      <td style={{ border: '1px solid #000', padding: '4px' }}>Fetal Movement</td>
                      <td style={{ border: '1px solid #000', padding: '4px' }}>{report.ultrasoundReport.fetalMovement}</td>
                    </tr>
                  )}
                  {report.ultrasoundReport.cardiacActivity && (
                    <tr>
                      <td style={{ border: '1px solid #000', padding: '4px' }}>Cardiac Activity</td>
                      <td style={{ border: '1px solid #000', padding: '4px' }}>{report.ultrasoundReport.cardiacActivity}</td>
                    </tr>
                  )}
                  {report.ultrasoundReport.placentaPosition && (
                    <tr>
                      <td style={{ border: '1px solid #000', padding: '4px' }}>Placenta Position</td>
                      <td style={{ border: '1px solid #000', padding: '4px' }}>{report.ultrasoundReport.placentaPosition}</td>
                    </tr>
                  )}
                  {report.ultrasoundReport.amniotic && (
                    <tr>
                      <td style={{ border: '1px solid #000', padding: '4px' }}>Amniotic Fluid</td>
                      <td style={{ border: '1px solid #000', padding: '4px' }}>{report.ultrasoundReport.amniotic}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
          
          {report.ultrasoundReport.type === 'Abdominal' && (
            <div style={{ marginBottom: '15px' }}>
              <h4 style={{ fontSize: '12px', marginBottom: '5px' }}>Organ-Specific Findings</h4>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
                <tbody>
                  {report.ultrasoundReport.liver && (
                    <tr>
                      <th style={{ border: '1px solid #ddd', padding: '3px', width: '30%', textAlign: 'left' }}>Liver</th>
                      <td style={{ border: '1px solid #ddd', padding: '3px' }}>{report.ultrasoundReport.liver}</td>
                    </tr>
                  )}
                  {report.ultrasoundReport.gallbladder && (
                    <tr>
                      <th style={{ border: '1px solid #ddd', padding: '3px', textAlign: 'left' }}>Gallbladder</th>
                      <td style={{ border: '1px solid #ddd', padding: '3px' }}>{report.ultrasoundReport.gallbladder}</td>
                    </tr>
                  )}
                  {report.ultrasoundReport.pancreas && (
                    <tr>
                      <th style={{ border: '1px solid #ddd', padding: '3px', textAlign: 'left' }}>Pancreas</th>
                      <td style={{ border: '1px solid #ddd', padding: '3px' }}>{report.ultrasoundReport.pancreas}</td>
                    </tr>
                  )}
                  {report.ultrasoundReport.spleen && (
                    <tr>
                      <th style={{ border: '1px solid #ddd', padding: '3px', textAlign: 'left' }}>Spleen</th>
                      <td style={{ border: '1px solid #ddd', padding: '3px' }}>{report.ultrasoundReport.spleen}</td>
                    </tr>
                  )}
                  {report.ultrasoundReport.kidneys && (
                    <tr>
                      <th style={{ border: '1px solid #ddd', padding: '3px', textAlign: 'left' }}>Kidneys</th>
                      <td style={{ border: '1px solid #ddd', padding: '3px' }}>{report.ultrasoundReport.kidneys}</td>
                    </tr>
                  )}
                  {report.ultrasoundReport.otherStructures && (
                    <tr>
                      <th style={{ border: '1px solid #ddd', padding: '3px', textAlign: 'left' }}>Other Structures</th>
                      <td style={{ border: '1px solid #ddd', padding: '3px' }}>{report.ultrasoundReport.otherStructures}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
          
          {report.ultrasoundReport.findings && (
            <div style={{ marginBottom: '15px' }}>
              <h4 style={{ fontSize: '12px', marginBottom: '5px' }}>Findings</h4>
              <p style={{ margin: '0', fontSize: '11px', border: '1px solid #ddd', padding: '5px' }}>
                {report.ultrasoundReport.findings}
              </p>
            </div>
          )}
          
          {/* Display ultrasound images */}
          {report.ultrasoundReport.images && report.ultrasoundReport.images.length > 0 && (
            <div className="ultrasound-images" style={{ width: '100%' }}>
              <h4 style={{ fontSize: '12px', marginBottom: '10px' }}>Ultrasound Images</h4>
              
              <table style={{ width: '100%', border: 'none', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                <tbody style={{ display: 'block', width: '100%' }}>
                  {Array.from({ length: Math.ceil(report.ultrasoundReport.images.length / 2) }).map((_, rowIndex) => (
                    <tr key={rowIndex} style={{ display: 'flex', width: '100%', flexDirection: 'row' }}>
                      {report.ultrasoundReport.images[rowIndex * 2] && (
                        <td style={{ width: '50%', display: 'inline-block', padding: '5px', textAlign: 'center', verticalAlign: 'top', float: 'left' }}>
                          <img
                            src={window.location.origin + report.ultrasoundReport.images[rowIndex * 2].path}
                            alt={`Ultrasound image ${rowIndex * 2 + 1}`}
                            style={{ 
                              width: '100%',
                              maxWidth: '100%',
                              height: 'auto',
                              objectFit: 'contain',
                              border: '1px solid #ddd',
                              padding: '4px',
                              backgroundColor: '#f8f9fa',
                              display: 'block',
                              margin: '0 auto'
                            }}
                          />
                          {report.ultrasoundReport.images[rowIndex * 2].caption && (
                            <p style={{ 
                              margin: '5px 0 0 0',
                              fontSize: '10px',
                              fontStyle: 'italic',
                              textAlign: 'center'
                            }}>
                              {report.ultrasoundReport.images[rowIndex * 2].caption}
                            </p>
                          )}
                        </td>
                      )}
                      
                      {report.ultrasoundReport.images[rowIndex * 2 + 1] && (
                        <td style={{ width: '50%', display: 'inline-block', padding: '5px', textAlign: 'center', verticalAlign: 'top', float: 'left' }}>
                          <img
                            src={window.location.origin + report.ultrasoundReport.images[rowIndex * 2 + 1].path}
                            alt={`Ultrasound image ${rowIndex * 2 + 2}`}
                            style={{ 
                              width: '100%',
                              maxWidth: '100%',
                              height: 'auto',
                              objectFit: 'contain',
                              border: '1px solid #ddd',
                              padding: '4px',
                              backgroundColor: '#f8f9fa',
                              display: 'block',
                              margin: '0 auto'
                            }}
                          />
                          {report.ultrasoundReport.images[rowIndex * 2 + 1].caption && (
                            <p style={{ 
                              margin: '5px 0 0 0',
                              fontSize: '10px',
                              fontStyle: 'italic',
                              textAlign: 'center'
                            }}>
                              {report.ultrasoundReport.images[rowIndex * 2 + 1].caption}
                            </p>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          
          {report.ultrasoundReport.impression && (
            <div style={{ marginBottom: '15px' }}>
              <h4 style={{ fontSize: '12px', marginBottom: '5px' }}>Impression</h4>
              <p style={{ margin: '0', fontSize: '11px', border: '1px solid #ddd', padding: '5px' }}>
                {report.ultrasoundReport.impression}
              </p>
            </div>
          )}
          
          {report.ultrasoundReport.recommendations && (
            <div style={{ marginBottom: '15px' }}>
              <h4 style={{ fontSize: '12px', marginBottom: '5px' }}>Recommendations</h4>
              <p style={{ margin: '0', fontSize: '11px', border: '1px solid #ddd', padding: '5px' }}>
                {report.ultrasoundReport.recommendations}
              </p>
            </div>
          )}
          
          {/* Doctor Signatures */}
          <div className="doctor-signatures" style={{ marginTop: '30px', textAlign: 'center' }}>
            <div style={{ display: 'flex', justifyContent: 'space-around' }}>
              {signedDoctors.map((doctor, index) => (
                <div key={index} style={{ width: '100px', textAlign: 'center', display: 'inline-block', margin: '0 10px' }}>
                  <div style={{ borderTop: '1px solid #000', width: '100px' }}></div>
                  <p style={{ margin: '5px 0 0 0', fontSize: '10px' }}>Dr. {doctor.name}</p>
                  <p style={{ margin: '0', fontSize: '9px' }}>{doctor.qualification || ''}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

export default PatientReportView;
